// Supabase REST API Client for Eldera Concierge Dashboard
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const RESIDENCE_ID = '11111111-1111-1111-1111-111111111111';

const headers = {
  'apikey': SUPABASE_ANON_KEY,
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  'Content-Type': 'application/json',
  'Prefer': 'return=representation'
};

// Helper for REST API calls
async function supabaseRest(endpoint, options = {}) {
  const url = `${SUPABASE_URL}/rest/v1${endpoint}`;
  const response = await fetch(url, {
    headers,
    ...options
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Supabase error: ${response.status} - ${error}`);
  }
  
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

// Q1: Get 6 apartments with residents
export async function getApartments() {
  return supabaseRest(
    `/apartments?residence_id=eq.${RESIDENCE_ID}&select=id,apt_label,floor,residents(id,first_name,last_name)&order=apt_label.asc`
  );
}

// Q2: Get device status (bracelets) by resident
export async function getDevices() {
  return supabaseRest(
    `/devices?device_type=eq.bracelet&select=id,resident_id,status,battery_percent,last_seen_at,rssi`
  );
}

// Q3: Get open alerts
export async function getOpenAlerts() {
  return supabaseRest(
    `/alerts?status=eq.open&select=id,resident_id,event_id,alert_type,severity,created_at,escalate_after_minutes&order=created_at.desc`
  );
}

// Q4: Get recent events (last 24h)
export async function getRecentEvents() {
  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  return supabaseRest(
    `/events?ts=gte.${twentyFourHoursAgo}&select=id,resident_id,event_type,ts,data&order=ts.desc`
  );
}

// Get all alerts (for logs page)
export async function getAllAlerts(limit = 100) {
  return supabaseRest(
    `/alerts?select=id,resident_id,event_id,alert_type,severity,status,created_at,acknowledged_at,acknowledged_by,resolved_at,resolution,resolution_note&order=created_at.desc&limit=${limit}`
  );
}

// Get all events (for logs page)
export async function getAllEvents(limit = 100) {
  return supabaseRest(
    `/events?select=id,resident_id,event_type,ts,data&order=ts.desc&limit=${limit}`
  );
}

// Get resident by ID
export async function getResident(residentId) {
  const residents = await supabaseRest(
    `/residents?id=eq.${residentId}&select=*`
  );
  return residents?.[0] || null;
}

// Get resident settings
export async function getResidentSettings(residentId) {
  const settings = await supabaseRest(
    `/resident_settings?resident_id=eq.${residentId}&select=*`
  );
  return settings?.[0] || null;
}

// Update resident settings
export async function updateResidentSettings(residentId, updates) {
  return supabaseRest(
    `/resident_settings?resident_id=eq.${residentId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(updates)
    }
  );
}

// Get resident history (events + alerts)
export async function getResidentHistory(residentId, limit = 50) {
  const [events, alerts] = await Promise.all([
    supabaseRest(`/events?resident_id=eq.${residentId}&select=*&order=ts.desc&limit=${limit}`),
    supabaseRest(`/alerts?resident_id=eq.${residentId}&select=*&order=created_at.desc&limit=${limit}`)
  ]);
  return { events: events || [], alerts: alerts || [] };
}

// ============ SIMULATION ACTIONS ============

// A) Simulate activity score
export async function simulateActivityScore(residentId, score = null) {
  const randomScore = score ?? Math.floor(Math.random() * 41) + 50; // 50-90
  return supabaseRest('/events', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_type: 'activity_score',
      data: { score: randomScore }
    })
  });
}

// B) Simulate fridge_open (Petit-déj)
export async function simulateFridgeOpen(residentId) {
  return supabaseRest('/events', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_type: 'fridge_open',
      data: {}
    })
  });
}

// C) Simulate IAQM normal
export async function simulateIAQMNormal(residentId) {
  return supabaseRest('/events', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_type: 'iaqm_reading',
      data: { temp_c: 21.6, hum_pct: 46, co2_ppm: 820 }
    })
  });
}

// D) Simulate IAQM CO2 high
export async function simulateIAQMHigh(residentId) {
  return supabaseRest('/events', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_type: 'iaqm_reading',
      data: { temp_c: 22.4, hum_pct: 55, co2_ppm: 1650 }
    })
  });
}

// E) Simulate tracker outside
export async function simulateTrackerOutside(residentId, outside = true) {
  return supabaseRest('/events', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_type: 'tracker_outside',
      data: { outside }
    })
  });
}

// F) Simulate bracelet offline
export async function simulateBraceletOffline(residentId) {
  return supabaseRest(
    `/devices?resident_id=eq.${residentId}&device_type=eq.bracelet`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'offline',
        last_seen_at: null
      })
    }
  );
}

// F) Simulate bracelet online
export async function simulateBraceletOnline(residentId) {
  return supabaseRest(
    `/devices?resident_id=eq.${residentId}&device_type=eq.bracelet`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'online',
        last_seen_at: new Date().toISOString(),
        battery_percent: 78
      })
    }
  );
}

// G) Simulate fall detected + create alert
export async function simulateFall(residentId) {
  // 1. Create fall event
  const eventResult = await supabaseRest('/events', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_type: 'fall_detected',
      data: { immobility_seconds: 52 }
    })
  });
  
  const eventId = eventResult?.[0]?.id;
  
  // 2. Create fall alert
  return supabaseRest('/alerts', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_id: eventId,
      alert_type: 'fall',
      severity: 'critical',
      status: 'open',
      escalate_after_minutes: 3
    })
  });
}

// H) Simulate medical need + create alert
export async function simulateMedical(residentId) {
  // 1. Create medical event
  const eventResult = await supabaseRest('/events', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_type: 'medical_need',
      data: { reason: 'Button pressed' }
    })
  });
  
  const eventId = eventResult?.[0]?.id;
  
  // 2. Create medical alert
  return supabaseRest('/alerts', {
    method: 'POST',
    body: JSON.stringify({
      resident_id: residentId,
      event_id: eventId,
      alert_type: 'medical',
      severity: 'high',
      status: 'open',
      escalate_after_minutes: 5
    })
  });
}

// I) Acknowledge alert
export async function acknowledgeAlert(alertId) {
  return supabaseRest(
    `/alerts?id=eq.${alertId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'acknowledged',
        acknowledged_at: new Date().toISOString(),
        acknowledged_by: 'concierge@eldera.lv'
      })
    }
  );
}

// J) Resolve alert
export async function resolveAlert(alertId, resolution, note = '') {
  return supabaseRest(
    `/alerts?id=eq.${alertId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'resolved',
        resolved_at: new Date().toISOString(),
        resolution,
        resolution_note: note
      })
    }
  );
}

// K) Reset demo resident
export async function resetDemoResident(residentId) {
  // 1. Close all open alerts for this resident
  await supabaseRest(
    `/alerts?resident_id=eq.${residentId}&status=in.(open,acknowledged)`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'resolved',
        resolved_at: new Date().toISOString(),
        resolution: 'false_alarm',
        resolution_note: 'Reset demo'
      })
    }
  );
  
  // 2. Set bracelet online
  await simulateBraceletOnline(residentId);
  
  return { success: true };
}

// Get all residents for dropdown
export async function getAllResidents() {
  return supabaseRest(
    `/residents?select=id,first_name,last_name,apartments(apt_label)&order=last_name.asc`
  );
}

export { RESIDENCE_ID };
